import { useEffect, useState } from "react";
import { Check, Zap, Star, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import RoleUpgradeDialog, { type UpgradeRole } from "@/components/platform/RoleUpgradeDialog";

const LandingPricingSection = () => {
  const navigate = useNavigate();
  const [signedIn, setSignedIn] = useState(false);
  const [upgradeRole, setUpgradeRole] = useState<UpgradeRole | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSignedIn(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => setSignedIn(!!session));
    return () => sub.subscription.unsubscribe();
  }, []);

  /** Paid tiers start a real Paystack checkout for signed-in users. */
  const startUpgrade = (tierName: string) => {
    const role = tierName.toLowerCase() as UpgradeRole;
    if (role !== "creator" && role !== "venue") {
      navigate("/auth");
      return;
    }
    if (!signedIn) {
      navigate("/auth");
      return;
    }
    setUpgradeRole(role);
  };

  const tiers = [
    {
      name: "Groover",
      price: "Free",
      description: "Everyone starts here — no card needed",
      icon: Zap,
      features: ["Browse events and venues", "Follow creators and venues", "Buy tickets and merch", "Event discovery", "Community access"],
      highlighted: false,
      note: "Included with every account",
    },
    {
      name: "Creator",
      price: "R249",
      period: "/month",
      description: "Add-on for artists and performers",
      icon: Star,
      features: ["Professional verified profile", "Music library hosting", "Booking management", "Earnings & payouts", "Groove studio tools", "Direct messaging"],
      highlighted: true,
      note: "Unlock anytime inside the app",
    },
    {
      name: "Venue",
      price: "R849",
      period: "/month",
      description: "Add-on for venue owners and managers",
      icon: Building2,
      features: ["Event calendar management", "Ticket sales & scanning", "Creator discovery", "Venue analytics", "Merch display", "Promotions tools"],
      highlighted: true,
      note: "Unlock anytime inside the app",
    },
  ];

  return (
    <section id="pricing" className="py-16 sm:py-28 bg-black text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black pointer-events-none" />
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <p className="text-xs sm:text-sm tracking-[0.2em] text-gray-500 uppercase font-medium mb-3">Platform Pricing</p>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Free to Join. Upgrade When You're Ready.</h2>
          <p className="mt-4 text-base sm:text-lg text-gray-400">
            Sign up free as a Groover — no payment at registration. Creator and Venue are paid add-ons you activate from your profile inside the app.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 max-w-6xl mx-auto">
          {tiers.map((tier) => {
            const IconComponent = tier.icon;
            return (
              <Card
                key={tier.name}
                className={`relative flex flex-col bg-black/60 backdrop-blur-sm border transition-all duration-300 md:hover:scale-[1.03] ${
                  tier.highlighted ? "border-white shadow-2xl shadow-white/10" : "border-white/20 hover:border-white/40"
                }`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-black px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                    In-app add-on
                  </div>
                )}
                <CardHeader className="pt-7">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2.5 sm:p-3 rounded-lg shrink-0 ${tier.highlighted ? "bg-white/10" : "bg-white/5"}`}>
                      <IconComponent className={`w-5 h-5 sm:w-6 sm:h-6 ${tier.highlighted ? "text-white" : "text-gray-400"}`} />
                    </div>
                    <CardTitle className="text-xl sm:text-2xl text-white">{tier.name}</CardTitle>
                  </div>
                  <CardDescription className="text-gray-400">{tier.description}</CardDescription>
                  <div className="mt-4">
                    <span className="text-3xl sm:text-4xl font-bold text-white">{tier.price}</span>
                    {tier.period && <span className="text-gray-500">{tier.period}</span>}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{tier.note}</p>
                </CardHeader>
                <CardContent className="flex-1">
                  <ul className="space-y-2.5">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="flex flex-col gap-3">
                  <Button
                    className={`w-full ${
                      tier.highlighted
                        ? "bg-white text-black hover:bg-gray-200"
                        : "bg-white/10 hover:bg-white/20 text-white border border-white/20"
                    }`}
                    onClick={() => startUpgrade(tier.name)}
                  >
                    {tier.name === "Groover"
                      ? "Create Free Account"
                      : signedIn
                        ? `Upgrade — ${tier.price}${tier.period ?? ""}`
                        : "Create Free Account"}
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full text-gray-400 hover:text-white"
                    onClick={() => navigate("/pricing")}
                  >
                    View Full Details
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>
      <RoleUpgradeDialog role={upgradeRole} onOpenChange={(open) => !open && setUpgradeRole(null)} />
    </section>
  );
};

export default LandingPricingSection;
